import { PasswordToggle } from "@/components/features";
import { CheckBoxDtos, ReCaptchaComponent } from "@/components/ui";
import { FaEnvelope, FaLock, FaPhoneAlt, FaRegIdCard } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { NewRegisterFormProps } from "../Types/types";

const FormRegister: React.FC<NewRegisterFormProps> = ({
    primer_nombre,
    segundo_nombre,
    primer_apellido,
    segundo_apellido,
    email,
    password,
    confirmPassword,
    emailError,
    passwordError,
    confirmPasswordError,
    acceptTerms,
    isCheckboxDisabled,
    isSubmitDisabled,
    tipoDocumento,
    numeroDocumento,
    numeroDocumentoError,
    celular,
    celularError,
    tiposIdentificacion,
    loadingTipos,
    loading,
    onPrimerNombreChange,
    onSegundoNombreChange,
    onPrimerApellidoChange,
    onSegundoApellidoChange,
    onEmailChange,
    onCelularChange,
    onTipoDocumentoChange,
    onNumeroDocumentoChange,
    onPasswordChange,
    onConfirmPasswordChange,
    onSubmit,
    onAcceptTermsChange,
    onRecaptchaVerify,
    onRecaptchaError,
    onRecaptchaExpired,
}) => {
    const documentosOptions = loadingTipos
        ? [{ value: "", label: "Cargando tipos..." }]
        : tiposIdentificacion.map((tipo) => ({
            value: tipo.nombre,
            label: tipo.nombreCompleto,
        }));

    const areNamesValid =
        primer_nombre.trim() !== "" && primer_apellido.trim() !== "";

    /* Fortaleza contraseña */
    const getStrength = (val: string) => {
        let s = 0;
        if (val.length >= 8) s++;
        if (/[A-Z]/.test(val)) s++;
        if (/[0-9]/.test(val)) s++;
        if (/[^A-Za-z0-9]/.test(val)) s++;
        return s;
    };
    const strength = getStrength(password);
    const strengthColor =
        strength <= 1
            ? "bg-red-400"
            : strength <= 2
                ? "bg-amber-400"
                : "bg-green-400";
    const strengthLabel =
        strength <= 1
            ? "Débil"
            : strength <= 2
                ? "Regular"
                : strength <= 3
                    ? "Buena"
                    : "Fuerte";

    return (
        <form
            key="register-form"
            className="flex flex-col gap-5 form-emerge"
            onSubmit={onSubmit}
        >
            {/* NOMBRES Y APELLIDOS */}
            <div className="relative space-y-2 p-0.5">
                <label className="text-lg font-bold block text-black">
                    Nombres y Apellidos <span className="text-[var(--color-error)]">*</span>
                </label>
                <div className="grid sm:grid-cols-2 gap-2 p-0.5">
                    <div className="relative">
                        <input
                            title="Primer Nombre"
                            type="text"
                            inputMode="text"
                            placeholder="Primer Nombre"
                            required
                            value={primer_nombre}
                            onChange={onPrimerNombreChange}
                            className="w-full px-3 py-2 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 text-gray-600 transition-colors duration-300"
                        />
                    </div>
                    <div className="relative">
                        <input
                            title="Segundo Nombre"
                            type="text"
                            inputMode="text"
                            placeholder="Segundo Nombre"
                            value={segundo_nombre}
                            onChange={onSegundoNombreChange}
                            className="w-full px-3 py-2 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 text-gray-600 transition-colors duration-300"
                        />
                    </div>
                    <div className="relative">
                        <input
                            title="Primer Apellido"
                            type="text"
                            inputMode="text"
                            placeholder="Primer Apellido"
                            required
                            value={primer_apellido}
                            onChange={onPrimerApellidoChange}
                            className="w-full px-3 py-2 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 text-gray-600 transition-colors duration-300"
                        />
                    </div>
                    <div className="relative">
                        <input
                            title="Segundo Apellido"
                            type="text"
                            inputMode="text"
                            placeholder="Segundo Apellido"
                            value={segundo_apellido}
                            onChange={onSegundoApellidoChange}
                            className="w-full px-3 py-2 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 border-gray-300 text-gray-600 transition-colors duration-300"
                        />
                    </div>
                </div>
            </div>

            {/* DOCUMENTO */}
            <div className="relative space-y-2 p-0.5">
                <label className="text-lg font-bold block text-black">
                    Documento de identificación <span className="text-[var(--color-error)]">*</span>
                </label>
                <div className="grid sm:grid-cols-2 gap-2 p-0.5">
                    <div className="relative">
                        <select
                            className={`w-full px-3 py-3 pr-10 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 border-gray-300 focus:ring-blue-400 appearance-none ${
                                !areNamesValid ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer"
                            }`}
                            value={tipoDocumento}
                            onChange={onTipoDocumentoChange}
                            disabled={!areNamesValid || loadingTipos}
                        >
                            <option value="">
                                {loadingTipos ? "Cargando..." : "Selecciona tipo"}
                            </option>
                            {documentosOptions.map((doc) => (
                                <option key={doc.value} value={doc.value}>
                                    {doc.label}
                                </option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xl">
                            <FiChevronDown />
                        </span>
                    </div>
                    <div className="relative">
                        <div className="flex gap-1">
                            <div className="flex-1 relative">
                                <input
                                    title="Número de documento"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Número de Documento"
                                    required
                                    value={numeroDocumento}
                                    onChange={(e) => {
                                        const onlyNumbers = e.target.value.replace(/\D/g, "");
                                        onNumeroDocumentoChange({
                                            ...e,
                                            target: { ...e.target, value: onlyNumbers },
                                        } as React.ChangeEvent<HTMLInputElement>);
                                    }}
                                    disabled={!tipoDocumento}
                                    className={`w-full pl-10 pr-4 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 ${
                                        numeroDocumentoError
                                            ? "border-red-500 focus:ring-red-400"
                                            : "border-gray-300 focus:ring-blue-400"
                                    } ${!tipoDocumento ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                />
                                <FaRegIdCard className="w-5 h-5 absolute left-3 top-1/2 -translate-y-3 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                        {numeroDocumentoError && (
                            <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                                {numeroDocumentoError}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* CORREO Y CELULAR */}
            <div className="grid sm:grid-cols-2 gap-2 p-0.5">
                <div className="relative p-0.5">
                    <label className="text-lg sm:text-base font-bold mb-1 block text-black">
                        Correo electrónico <span className="text-[var(--color-error)]">*</span>
                    </label>
                    <div className="relative p-0.5">
                        <input
                            title="Correo"
                            type="email"
                            placeholder="tucorreo@mail.com"
                            required
                            value={email}
                            onChange={onEmailChange}
                            disabled={!numeroDocumento || !!numeroDocumentoError}
                            className={`w-full pl-12 pr-4 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 ${
                                emailError
                                    ? "border-red-500 focus:ring-red-400"
                                    : "border-gray-300 focus:ring-blue-400"
                            } ${!numeroDocumento ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        />
                        <FaEnvelope className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    {emailError && (
                        <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                            {emailError}
                        </p>
                    )}
                </div>
                <div className="relative space-y-1">
                    <label className="text-lg sm:text-base font-bold block text-black">
                        Celular <span className="text-[var(--color-error)]">*</span>
                    </label>
                    <div className="relative">
                        <div className="flex gap-1">
                            <div className="flex-1 relative">
                                <input
                                    title="Número de celular"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Número de celular"
                                    required
                                    value={celular}
                                    onChange={(e) => {
                                        const onlyNumbers = e.target.value.replace(/\D/g, "");
                                        onCelularChange({
                                            ...e,
                                            target: { ...e.target, value: onlyNumbers },
                                        } as React.ChangeEvent<HTMLInputElement>);
                                    }}
                                    disabled={!email || !!emailError}
                                    className={`w-full pl-12 pr-4 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 ${
                                        celularError
                                            ? "border-red-500 focus:ring-red-400"
                                            : "border-gray-300 focus:ring-blue-400"
                                    } ${!email ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                />
                                <FaPhoneAlt className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>
                    {celularError && (
                        <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                            {celularError}
                        </p>
                    )}
                </div>
            </div>

            {/* CONTRASEÑAS */}
            <div className="grid sm:grid-cols-2 gap-2 p-0.5">
                <div className="relative p-0.5">
                    <label className="text-lg sm:text-base font-bold mb-1 block text-black">
                        Contraseña <span className="text-[var(--color-error)]">*</span>
                    </label>
                    <div className="relative p-0.5">
                        <PasswordToggle
                            label="Contraseña"
                            placeholder="Contraseña"
                            required={true}
                            name="password"
                            value={password}
                            onChange={onPasswordChange}
                            disabled={!celular || !!celularError}
                            className={`w-full pl-12 pr-14 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 border-gray-300 focus:ring-blue-400 ${
                                !celular ? "bg-gray-100 cursor-not-allowed" : ""
                            }`}
                        />
                        <FaLock className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    {passwordError && (
                        <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                            {passwordError}
                        </p>
                    )}
                    {/* Strength */}
                    {password.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                            <div className="flex gap-1 flex-1">
                                {[1, 2, 3, 4].map((i) => (
                                    <div
                                        key={i}
                                        className={`flex-1 h-1 rounded-full transition-all duration-300 ${i <= strength ? strengthColor : "bg-[var(--color-border)]"}`}
                                    />
                                ))}
                            </div>
                            <span
                                className={`text-[10px] font-semibold ${strength <= 1 ? "text-red-400" : strength <= 2 ? "text-amber-400" : "text-green-500"}`}
                            >
                                {strengthLabel}
                            </span>
                        </div>
                    )}
                </div>
                <div className="relative p-0.5">
                    <label className="text-lg sm:text-base font-bold mb-1 block text-black">
                        Confirma tu contraseña <span className="text-[var(--color-error)]">*</span>
                    </label>
                    <div className="relative p-0.5">
                        <PasswordToggle
                            label="Confirmar Contraseña"
                            placeholder="Confirmar Contraseña"
                            required={true}
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={onConfirmPasswordChange}
                            disabled={!password || !!passwordError}
                            className={`w-full pl-12 pr-14 py-3 border rounded-2xl text-xl bg-white focus:outline-none focus:ring-2 transition duration-300 text-gray-600 border-gray-300 focus:ring-blue-400 ${
                                !password ? "bg-gray-100 cursor-not-allowed" : ""
                            }`}
                        />
                        <FaLock className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    {confirmPasswordError && (
                        <p className="mt-1 text-xs sm:text-sm text-[var(--color-error)] font-medium">
                            {confirmPasswordError}
                        </p>
                    )}
                </div>
            </div>

            {/* TÉRMINOS */}
            <CheckBoxDtos
                checked={acceptTerms}
                disabled={isCheckboxDisabled}
                onChange={onAcceptTermsChange}
                required={true}
            />

            {/* RECAPTCHA */}
            <ReCaptchaComponent
                onVerify={onRecaptchaVerify}
                onError={onRecaptchaError}
                onExpired={onRecaptchaExpired}
                disabled={!acceptTerms}
            />

            {/* BOTÓN */}
            <button
                type="submit"
                disabled={isSubmitDisabled || loading}
                className={`w-full py-3 sm:py-5 font-bold rounded-2xl text-xl transition duration-300 shadow-md ${
                    !isSubmitDisabled && !loading
                        ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-600)] hover:shadow-lg shadow-blue-400/30 cursor-pointer"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed shadow-gray-300/30"
                }`}
            >
                {loading ? "Registrando..." : "Crear mi cuenta"}
            </button>
        </form>
    );
};

export default FormRegister;
